import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SyncIcon from '@mui/icons-material/Sync';
import InventoryIcon from '@mui/icons-material/Inventory';
import PlateformService from '../../components/PlateformService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const formatDateTime = (date) =>
  date instanceof Date ? date.toLocaleString() : null;

function KooulPlatformConsole() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialPlateformId = location?.state?.plateformId || '';
  const initialPlateformName = location?.state?.plateformName || '';

  const [plateformId, setPlateformId] = useState(initialPlateformId);
  const [isSyncingCategories, setIsSyncingCategories] = useState(false);
  const [lastCategoriesSync, setLastCategoriesSync] = useState(null);
  const [isSyncingProducts, setIsSyncingProducts] = useState(false);
  const [lastProductsSync, setLastProductsSync] = useState(null);
  const [productSyncResult, setProductSyncResult] = useState(null);

  const trimmedPlateformId = useMemo(() => plateformId.trim(), [plateformId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!trimmedPlateformId) {
      toast.error("Veuillez renseigner l'identifiant de la plateforme Kooul.");
      return;
    }

    try {
      setIsSyncingCategories(true);
      const response = await PlateformService.syncKooulCategories({
        plateform_id: trimmedPlateformId,
      });

      const message =
        (response && typeof response === 'object' && response.message) ||
        'Synchronisation des catégories Kooul réussie.';

      toast.success(message);
      setLastCategoriesSync(new Date());
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        'Échec de la synchronisation des catégories Kooul.';
      toast.error(message);
    } finally {
      setIsSyncingCategories(false);
    }
  };

  const handleSyncProducts = async () => {
    if (!trimmedPlateformId) {
      toast.error("Veuillez renseigner l'identifiant de la plateforme Kooul.");
      return;
    }

    try {
      setIsSyncingProducts(true);
      setProductSyncResult(null);
      const response = await PlateformService.syncKooulAllProducts({
        plateform_id: trimmedPlateformId,
      });

      const message =
        (response && typeof response === 'object' && response.message) ||
        'Synchronisation des produits Kooul réussie.';

      toast.success(message);
      setProductSyncResult(response);
      setLastProductsSync(new Date());
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        'Échec de la synchronisation des produits Kooul.';
      toast.error(message);
    } finally {
      setIsSyncingProducts(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Retour
          </Button>
          <Box>
            <Typography variant="h4" component="h1">
              Plateforme Kooul
            </Typography>
            {initialPlateformName && (
              <Typography variant="subtitle1" color="text.secondary">
                {initialPlateformName}
              </Typography>
            )}
          </Box>
        </Stack>

        <Card variant="outlined">
          <form onSubmit={handleSubmit}>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Synchroniser les catégories
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Déclenchez la synchronisation des catégories Kooul pour la
                    plateforme sélectionnée.
                  </Typography>
                </Box>

                <TextField
                  label="Identifiant de la plateforme"
                  value={plateformId}
                  onChange={(event) => setPlateformId(event.target.value)}
                  required
                  helperText="Utilisez l'identifiant Kooul stocké dans la plateforme."
                />

                {lastCategoriesSync && (
                  <Typography variant="caption" color="text.secondary">
                    Dernière synchronisation : {formatDateTime(lastCategoriesSync)}
                  </Typography>
                )}
              </Stack>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SyncIcon />}
                disabled={isSyncingCategories}
              >
                {isSyncingCategories
                  ? 'Synchronisation en cours…'
                  : 'Synchroniser les catégories'}
              </Button>
            </CardActions>
          </form>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" component="h2" gutterBottom>
                  Synchroniser tous les produits
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Lancez la synchronisation complète des produits Kooul pour la
                  plateforme sélectionnée. Cette opération récupère les
                  produits pour chaque catégorie distante disponible.
                </Typography>
              </Box>

              {lastProductsSync && (
                <Typography variant="caption" color="text.secondary">
                  Dernière synchronisation produits :{' '}
                  {formatDateTime(lastProductsSync)}
                </Typography>
              )}

              {productSyncResult && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Résultat de la dernière synchronisation
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      p: 2,
                      overflow: 'auto',
                      maxHeight: 300,
                    }}
                  >
                    {JSON.stringify(productSyncResult, null, 2)}
                  </Box>
                </Box>
              )}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<InventoryIcon />}
              onClick={handleSyncProducts}
              disabled={isSyncingProducts}
            >
              {isSyncingProducts
                ? 'Synchronisation des produits…'
                : 'Synchroniser tous les produits'}
            </Button>
          </CardActions>
        </Card>
      </Stack>
    </Container>
  );
}

export default KooulPlatformConsole;
